// src/transport.ts
import { base64ToArrayBuffer, filterPII, log, warn } from './utils';
import type { OutgoingPayload, UserProfile, TransportErrorInfo, TransportSuccessInfo, RatePolicy, ProfileResponse, IdentifyEnvelope } from './types';
import { deleteOfflineItems, loadOfflineBatch, saveOfflineItem, type OfflineOptions } from './offline';

type TransportConfig = {
    endpoint: string;
    projectKey: string;
    hmacPublicKey?: string; // base64; optional
    debug?: boolean;
    authSecret?: string;
    authTtlSec?: number;
    offline?: Partial<OfflineOptions>;
    observability?: {
        onTransportError?: (info: TransportErrorInfo) => void;
        onTransportSuccess?: (info: TransportSuccessInfo) => void;
    };
    getAuthContext?: () => { userId?: string | null; sessionId?: string | null };
};

type QueueItem = {
    payload: OutgoingPayload;
    offlineKey?: number;
    offlineKeyPromise?: Promise<number | undefined>;
};

type IdentifyResult = { ok: boolean; ratePolicy?: RatePolicy };

let cfg: TransportConfig;
let queue: QueueItem[] = [];
let flushing = false;
let offlineCfg: OfflineOptions = { enabled: true, maxItems: 1000, maxAgeSec: 7 * 24 * 3600 };
let onTransportError: ((info: TransportErrorInfo) => void) | undefined;
let onTransportSuccess: ((info: TransportSuccessInfo) => void) | undefined;
let accessToken: string | undefined;
let transportDisabled = false;
const IDENTIFY_TIMEOUT_MS = 8000;

const getCurrentSessionId = () => cfg.getAuthContext?.().sessionId ?? null;

const toBase64Url = (input: string | ArrayBuffer | Uint8Array) => {
    const bytes = typeof input === 'string'
        ? new TextEncoder().encode(input)
        : input instanceof ArrayBuffer
            ? new Uint8Array(input)
            : input;
    let str = '';
    bytes.forEach((b) => { str += String.fromCharCode(b); });
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const jwtSegment = (obj: any) => toBase64Url(JSON.stringify(obj));

async function buildJwt(): Promise<string | undefined> {
    if (!cfg.authSecret || !('crypto' in window) || !crypto.subtle) return undefined;
    try {
        const ctx = cfg.getAuthContext?.() || {};
        const nowSec = Math.floor(Date.now() / 1000);
        const header = { alg: 'HS256', typ: 'JWT' };
        const payload: Record<string, unknown> = {
            sub: ctx.userId ?? cfg.projectKey,
            iat: nowSec,
            exp: nowSec + (cfg.authTtlSec ?? 600),
        };
        if (ctx.sessionId) payload.sid = ctx.sessionId;
        const data = `${jwtSegment(header)}.${jwtSegment(payload)}`;
        const key = await crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(cfg.authSecret),
            { name: 'HMAC', hash: 'SHA-256' },
            false,
            ['sign']
        );
        const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(data));
        return `${data}.${toBase64Url(new Uint8Array(sig))}`;
    } catch {
        return undefined;
    }
}

async function getAuthToken(): Promise<string | undefined> {
    if (accessToken) return accessToken;
    return undefined;
}

async function sign(body: string): Promise<string | undefined> {
    if (!cfg.hmacPublicKey || !('crypto' in window)) return undefined;
    try {
        const keyData = base64ToArrayBuffer(cfg.hmacPublicKey);
        const key = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
        const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(body));
        return btoa(String.fromCharCode(...new Uint8Array(sig)));
    } catch {
        return undefined;
    }
}

async function postBatch(items: QueueItem[]) {
    if (transportDisabled) return false;
    const sanitized = items.map((i) => filterPII(i.payload));
    const authToken = await getAuthToken();
    const bodyObj: Record<string, unknown> = { projectKey: cfg.projectKey, items: sanitized };
    if (authToken) bodyObj.access_token = authToken; // fallback si header ignoré
    const body = JSON.stringify(bodyObj);
    const sig = await sign(body);

    // Beacon si pas d'auth (pas de header possible)
    if (!authToken && navigator.sendBeacon && !cfg.debug) {
        const headers = { type: 'application/json' };
        const blob = new Blob([body], headers);
        const ok = navigator.sendBeacon(cfg.endpoint + '/ingest', blob);
        if (ok) {
            onTransportSuccess?.({ endpoint: cfg.endpoint + '/ingest', count: items.length });
            return true;
        }
    }

    // Fetch (permet Authorization)
    const res = await fetch(cfg.endpoint + '/ingest', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': cfg.projectKey,
            ...(sig ? { 'x-signature': sig } : {}),
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body
    });
    if (res.ok) {
        onTransportSuccess?.({ endpoint: cfg.endpoint + '/ingest', status: res.status, count: items.length });
        return true;
    }
    const detail = await res.text().catch(() => undefined);
    onTransportError?.({ endpoint: cfg.endpoint + '/ingest', status: res.status, reason: 'http_error', detail });
    return false;
}

export function initTransport(config: TransportConfig) {
    cfg = config;
    offlineCfg = { ...offlineCfg, ...(config.offline || {}) };
    onTransportError = config.observability?.onTransportError;
    onTransportSuccess = config.observability?.onTransportSuccess;
    window.addEventListener('online', flush);
    setInterval(flush, 5000);
}

export function setTransportDisabled(disabled: boolean) {
    transportDisabled = disabled;
    if (disabled) queue = [];
}

export async function postIdentify(profile: UserProfile, sessionId: string | null, userId?: string | null) {
    if (!sessionId) return { ok: false } as IdentifyResult;
    const bodyObj: Record<string, unknown> = {
        sessionId,
        userId: userId ?? undefined,
        profile,
    };
    const body = JSON.stringify(bodyObj);
    const sig = await sign(body);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), IDENTIFY_TIMEOUT_MS);
    let res: Response;
    try {
        res = await fetch(cfg.endpoint + '/identify', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': cfg.projectKey,
                ...(sig ? { 'x-signature': sig } : {}),
            },
            body,
            signal: controller.signal,
        });
    } catch (e) {
        const reason = (e as any)?.name === 'AbortError' ? 'identify_timeout' : 'exception';
        onTransportError?.({ endpoint: cfg.endpoint + '/identify', reason, detail: String(e) });
        return { ok: false } as IdentifyResult;
    } finally {
        clearTimeout(timer);
    }
    if (res.ok) {
        const json = (await res.json().catch(() => undefined)) as IdentifyEnvelope | ProfileResponse | undefined;
        const data = (json as IdentifyEnvelope | undefined)?.data ?? (json as ProfileResponse | undefined);
        if (data?.access_token) {
            accessToken = data.access_token;
        } else {
            warn('/identify succeeded but no access_token returned');
            transportDisabled = true;
        }
        onTransportSuccess?.({ endpoint: cfg.endpoint + '/identify', status: res.status });
        return { ok: true, ratePolicy: data?.ratePolicy } as IdentifyResult;
    }
    const detail = await res.text().catch(() => undefined);
    onTransportError?.({ endpoint: cfg.endpoint + '/identify', status: res.status, reason: 'http_error', detail });
    return { ok: false } as IdentifyResult;
}

export function enqueue(payload: OutgoingPayload) {
    if (transportDisabled) return;
    const currentSessionId = getCurrentSessionId();
    if (currentSessionId && 'sessionId' in payload && payload.sessionId !== currentSessionId) {
        return;
    }
    const item: QueueItem = { payload };
    if (offlineCfg.enabled) {
        item.offlineKeyPromise = saveOfflineItem(payload, offlineCfg);
    }
    queue.push(item);
    if (queue.length >= 10) void flush();
}

export async function flush() {
    if (flushing) return;
    if (transportDisabled) return;
    flushing = true;
    try {
        const currentSessionId = getCurrentSessionId();
        if (queue.length === 0 && offlineCfg.enabled) {
            const persisted = await loadOfflineBatch(20, offlineCfg);
            persisted.forEach((p) => queue.push({ payload: p.payload, offlineKey: p.key }));
        }
        if (currentSessionId) {
            const dropKeys: number[] = [];
            queue = queue.filter((item) => {
                const sid = (item.payload as any)?.sessionId;
                if (sid && sid !== currentSessionId) {
                    if (typeof item.offlineKey === 'number') dropKeys.push(item.offlineKey);
                    return false;
                }
                return true;
            });
            if (offlineCfg.enabled && dropKeys.length > 0) {
                await deleteOfflineItems(dropKeys);
            }
        }
        if (queue.length === 0) return;
        const batch = queue.splice(0, 20);
        const ok = await postBatch(batch);
        if (!ok) {
            queue.unshift(...batch);
            log('flush failed, will retry');
            return;
        }
        if (offlineCfg.enabled) {
            const keys = await Promise.all(batch.map(async (item) => {
                if (typeof item.offlineKey === 'number') return item.offlineKey;
                if (item.offlineKeyPromise) return await item.offlineKeyPromise;
                return undefined;
            }));
            const deleteKeys = keys.filter((k): k is number => typeof k === 'number');
            await deleteOfflineItems(deleteKeys);
        }
    } catch (e) {
        onTransportError?.({ endpoint: cfg.endpoint + '/ingest', reason: 'exception', detail: String(e) });
        log('flush error', e);
    } finally {
        flushing = false;
    }
}
